import { useIsMutating } from '@tanstack/react-query';
import React, { useState, useEffect, Fragment, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import insightsClient from 'search-insights';

import LoadingSvg from '@/components/common/LoadingSvg';
import EventBus from '@/components/pdp/PdpEventBus';
import { useAuthenticated } from '@/components/shopping_bag/hooks/useAuthenticated';
import { useCart, useRemoveItem, useUpdateCart } from '@/components/shopping_bag/hooks/useCart';
import { getShowBagEmpty } from '@/components/shopping_bag/utils/isBagEmpty';
import { Sheet, SheetContent, SheetDescription, SheetHeader } from '@/components/ui/sheet';
import { TmCartLine } from '@/tscapi/model/cart';
import { isConsentCategoryDisabled } from '@/tscapi/utils/consent';
import { Cookie } from '@/tscapi/utils/cookie';
import { cn } from '@/tscapi/utils/utils';

import FraudModal from '../v2/FraudModal/FraudModal';

import FreeShippingEarned from './components/FreeShippingEarned';
import FreeShippingProgress from './components/FreeShippingProgress';
import { ItemCard } from './components/ItemCard';
import { UpsellItemCard } from './components/UpsellItemCard';
import { en, fr } from './i18n';

const AddToBag = () => {
    const { i18n, t } = useTranslation('addtobag');
    i18n.addResourceBundle('en', 'addtobag', en, true, true);
    i18n.addResourceBundle('fr', 'addtobag', fr, true, true);
    const cookie: Cookie = new Cookie();
    const [cartLine, setCartLine] = useState<TmCartLine | null>(null);
    const [quantity, setQuantity] = useState(0);
    const [currentCartCount, setCurrentCartCount] = useState(null);
    const [edpNo, setEdpNo] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [donationInCart, setDonationInCart] = useState(false);
    const [showFraudModal, setShowFraudModal] = useState(false);
    const isAuthenticated = useAuthenticated();
    const { data: cart } = useCart(isAuthenticated);
    const isAutoFreeShipApplied = cart?.OrderSummary?.IsAutoFreeShipApplied;
    const isRogersCCFreeShipApplied = cart?.OrderSummary?.IsRogersCCFreeShipApplied;
    const freeShippingThresholdNumber = Number(window?.tscConfig?.FreeShippingThreshold);
    const autoFreeShipAwayFromThresholdAmount = Number(cart?.OrderSummary?.AutoFreeShipAwayFromThresholdAmount);

    const difference =
        autoFreeShipAwayFromThresholdAmount == 0
            ? 0
            : freeShippingThresholdNumber - autoFreeShipAwayFromThresholdAmount;
    const percentage = Math.min(Math.round((difference / freeShippingThresholdNumber) * 100), 100);
    const [delayedPercentage, setDelayedPercentage] = useState(0);
    const mutation = useRemoveItem(cart);
    const isMutating = useIsMutating();
    const updateMutation = useUpdateCart(cookie?.UserAccessToken?.Edp, cookie?.CartGuid);
    const currentDate = new Date();
    const hasMounted = useRef(false);
    const uniqueId =
        currentDate.getFullYear().toString() +
        (currentDate.getMonth() + 1).toString().padStart(2, '0') +
        currentDate.getDate().toString().padStart(2, '0') +
        currentDate.getHours().toString().padStart(2, '0') +
        currentDate.getMinutes().toString().padStart(2, '0') +
        currentDate.getSeconds().toString().padStart(2, '0');
    const [esiEventId, setESIEventId] = useState(parseInt(uniqueId));
    const prevIsOpen = useRef(isOpen);
    let encrypted = 'unidentifiable';
    let authencrypted = 'unidentifiable';
    async function digestMessage(message) {
        const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8); // hash the message
        const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
        return hashHex;
    }
    async function getECID() {
        if (typeof window.alloy !== 'undefined') {
            try {
                const result = await window.alloy('getIdentity');
                return result?.identity?.ECID || 'unidentifiable';
            } catch (error) {
                return 'unidentifiable';
            }
        } else {
            return 'unidentifiable';
        }
    }
    // check if the user is logged in
    async function getUser() {
        if (isConsentCategoryDisabled('functional')) {
            insightsClient('setUserToken', 'anonymous');
            console.log('Algolia insights disabled');
            return;
        }
        // if the user is logged in, set the auth user token
        if (cookie.UserEmail && cookie.UserAccessToken?.Token?.length > 0 && cookie.UserAccessToken.Edp > 0) {
            authencrypted = await digestMessage(cookie.UserEmail);
            insightsClient('setAuthenticatedUserToken', authencrypted);
        }
        encrypted = await getECID();
        insightsClient('setUserToken', encrypted);
        localStorage.setItem('ALGOLIA_USER_TOKEN', encrypted);
    }

    const hasQuantityToBeReducedMessage = () => {
        return cart?.CartLines?.some((line) => line?.InCart?.QuantityToBeReducedMessage) || false;
    };

    const checkout = () => {
        if (cart && cart.Buyer != null && cart.Buyer.IsFraud) {
            setIsOpen(false);
            setShowFraudModal(true);
        } else if (
            (cart.CartMessages &&
                (cart.CartMessages?.OutOfStockMessage ||
                    cart.CartMessages?.MaxCheckoutLimitReached ||
                    cart.CartMessages?.MaxCartLineCountReached ||
                    cart.CartMessages?.MaxQuantityPerCartLineReached)) ||
            hasQuantityToBeReducedMessage()
        ) {
            window.location.href = '/pages/shoppingbag';
        } else {
            window.location.href = '/pages/expresscheckout';
        }
    };

    useEffect(() => {
        let timer;
        if (prevIsOpen.current === false && isOpen === true) {
            //delayed open
            timer = setTimeout(() => {
                setDelayedPercentage(percentage);
            }, 600);
        } else {
            setDelayedPercentage(percentage);
        }
        prevIsOpen.current = isOpen;
        // Cleanup function to clear the timer
        return () => clearTimeout(timer);
    }, [isOpen, percentage]);

    useEffect(() => {
        if (hasMounted.current) {
            // Fire ESI event
            // Create a custom event with the new event ID as detail
            const event = new CustomEvent('addBagEvent', {
                detail: { eventId: esiEventId, cart: cart, edpNo: edpNo, quantity: quantity },
            });
            // Dispatch the event
            document.dispatchEvent(event);
        }
    }, [esiEventId]);

    const incrementESIEventId = () => {
        setESIEventId((prevEventId) => prevEventId + 1);
    };
    const trackConversion = () => {
        try {
            const algoliaClickData = JSON.parse(window.sessionStorage.getItem('TSC_AlgoliaClick')) || null;
            const currentItemNo = window.analyticsData.product.ItemNo;

            if (algoliaClickData) {
                if (algoliaClickData.objectIDs[0] == currentItemNo) {
                    //if current product matches last tracked searched item, fire a conversion event
                    setTimeout(() => {
                        insightsClient('addedToCartObjectIDsAfterSearch', {
                            eventName: 'Add To Cart',
                            index: 'products',
                            objectIDs: [algoliaClickData.objectIDs[0]],
                            queryID: algoliaClickData.queryID ? algoliaClickData.queryID : '',
                        });
                    }, 2000);
                    try {
                        window.sessionStorage.removeItem('TSC_AlgoliaClick');
                    } catch (error) {
                        // if it fails
                    }

                    try {
                        const existingTrackedItems =
                            JSON.parse(window.sessionStorage.getItem('TSC_AlgoliaCartItems')) || [];
                        const isAlreadyTracked = existingTrackedItems.some((item) => item.objectID === currentItemNo);

                        if (!isAlreadyTracked) {
                            const newItem = {
                                index: 'products',
                                objectID: currentItemNo,
                                position: 1,
                                queryID: algoliaClickData.queryID || '',
                            };

                            const updatedTrackedItems = [...existingTrackedItems, newItem];
                            window.sessionStorage.setItem('TSC_AlgoliaCartItems', JSON.stringify(updatedTrackedItems));
                        }
                    } catch (error) {
                        console.error('Error storing tracked item:', error);
                    }
                }
            }
        } catch (error) {
            // if it fails
        }
    };

    useEffect(() => {
        const processCartData = () => {
            let cartLineClone: TmCartLine;
            if (cart != null && cart?.CartLines?.length > 0) {
                const donationFilteredCart = cart?.CartLines.filter((item) => {
                    return !item?.CartLineItem?.IsDonation;
                });
                if (edpNo) {
                    //item added to bag

                    //find cartline of edp added to bag
                    cartLineClone =
                        cart.CartLines[cart.CartLines.findIndex((c) => c.CartLineItem.EdpNo.toString() == edpNo)];

                    setCartLine(cartLineClone);
                    //count items already in bag
                    const count = donationFilteredCart.filter((item) => {
                        if (item?.CartLineItem.EdpNo == edpNo) {
                            return item.InCart.Quantity - quantity > 0;
                        }
                        return true;
                    });
                    setCurrentCartCount(count.length);
                } else {
                    //items already exist in cart
                    //count items already in bag
                    setCurrentCartCount(donationFilteredCart.length);
                    setCartLine(null);
                }
                setDonationInCart(cart.CartLines.some((line) => line.CartLineItem.IsDonation === true));
                if (getShowBagEmpty(cart)) {
                    setIsOpen(false);
                }
            } else {
                if (getShowBagEmpty(cart)) {
                    setIsOpen(false);
                }
            }
        };
        processCartData();
    }, [cart, edpNo, quantity]);

    useEffect(() => {
        const waitUntil = (condition: () => boolean): Promise<void> => {
            return new Promise<void>((resolve, reject) => {
                const interval = setInterval(() => {
                    if (condition()) {
                        clearInterval(interval); // Stop checking the condition
                        clearTimeout(timeout); // Clear the timeout to avoid race conditions
                        resolve(); // Resolve the promise when the condition is true
                    }
                }, 100); // Check every 500ms for better responsiveness

                const timeout = setTimeout(() => {
                    clearInterval(interval); // Stop checking the condition
                    reject('Condition not met within timeout'); // Reject after timeout
                }, 1500); // Set a longer timeout (e.g., 5000ms) to allow more time for the condition
            });
        };

        waitUntil(() => typeof window.alloy !== 'undefined')
            .catch((error) => {
                console.warn('Alloy not loaded');
            })
            .finally(async () => {
                await getUser();
            });

        const handleAnalytics = (data) => {
            if (data != undefined) {
                trackConversion();
                setIsOpen(true);
                setEdpNo(data.edp);
                setQuantity(data.qty);
                //send analytic event odd to cart
                window.analyticsData.productChange = {};
                window.analyticsData.productChange.sku = data.edp;
                window.analyticsData.productChange.quantity = data.qty;
                window.analyticsData.event.eventTitle = 'change-in-cart';
                //for esi promocode

                incrementESIEventId();
                hasMounted.current = true;
            }
        };
        // Listen for the custom event from pdp to trigger addtocart sidebar
        EventBus.on('change', handleAnalytics);
        // returned function will be called on component unmount
        return () => {
            EventBus.remove('change', handleAnalytics);
            setDelayedPercentage(0);
            hasMounted.current = false;
        };
    }, []);

    const HideProgressBar = () => <div className="h-11">&nbsp;</div>;

    if (cart == null) {
        return null;
    }

    return (
        <>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetContent>
                    <div className="h-dvh-fallback flex flex-col justify-between">
                        <div className="m-0 flex-grow overflow-y-auto">
                            <div className="m-8">
                                {cartLine && edpNo && cartLine.CartLineItem && (
                                    <>
                                        <SheetHeader className="text-left">
                                            <SheetDescription className="text-base font-semibold text-black">
                                                Added to your bag
                                            </SheetDescription>
                                        </SheetHeader>
                                        <ItemCard
                                            CartLine={cartLine}
                                            CartProducts={cart.Products}
                                            QuantityAddedOveride={quantity}
                                            Remove={() => {
                                                // if item.Incart.Quantity - quantity > 0
                                                // do a cart update instead of a remove lineitem
                                                {
                                                    if (cartLine.InCart.Quantity - quantity > 0) {
                                                        updateMutation.mutate(
                                                            {
                                                                lineId: cartLine.Id,
                                                                edpNo: edpNo,
                                                                qty: cartLine.InCart.Quantity - quantity,
                                                                addRemove: quantity,
                                                            },
                                                            {
                                                                onSuccess: (data) => {
                                                                    incrementESIEventId();
                                                                    setEdpNo(null);
                                                                    setQuantity(null);
                                                                },
                                                            },
                                                        );
                                                    } else {
                                                        mutation.mutate(cartLine.Id, {
                                                            onSuccess: (data) => {
                                                                incrementESIEventId();
                                                                setEdpNo(null);
                                                                setQuantity(null);
                                                            },
                                                        });
                                                    }
                                                }
                                            }}
                                            Update={null}
                                        />
                                    </>
                                )}

                                {cart?.UpsellProducts?.length > 0 && (
                                    <>
                                        <div className="mb-5 border-0 border-b border-solid border-black"></div>
                                        <UpsellItemCard UpsellProducts={cart.UpsellProducts} />
                                    </>
                                )}

                                {cart?.CartLines?.length > 0 && currentCartCount != null && currentCartCount > 0 && (
                                    <>
                                        {/* Show border only if there are no upsell products, otherwise upsell section already has one */}
                                        {cart?.UpsellProducts?.length === 0 && (
                                            <div className="mb-5 border-0 border-b border-solid border-black"></div>
                                        )}
                                        <div className="mb-5 text-base font-semibold text-black">
                                            Currently in your bag
                                        </div>

                                        {cart?.CartLines.filter((item) => !item.CartLineItem.IsDonation).map((item) => {
                                            if (item?.CartLineItem.EdpNo == edpNo) {
                                                if (item.InCart.Quantity - quantity <= 0) {
                                                    // Don't display <ItemCard/> because it was shown above
                                                    return null;
                                                } else {
                                                    // Show <ItemCard/> with qty adjusted qty
                                                    return (
                                                        <Fragment key={item?.CartLineItem.EdpNo}>
                                                            <ItemCard
                                                                CartLine={item}
                                                                CartProducts={cart.Products}
                                                                QuantityAddedOveride={item.InCart.Quantity - quantity}
                                                                Remove={() => {
                                                                    if (cartLine.InCart.Quantity - quantity > 0) {
                                                                        updateMutation.mutate(
                                                                            {
                                                                                lineId: cartLine.Id,
                                                                                edpNo: edpNo,
                                                                                qty: quantity,
                                                                                addRemove: quantity,
                                                                            },
                                                                            {
                                                                                onSuccess: (data) => {
                                                                                    incrementESIEventId();
                                                                                },
                                                                            },
                                                                        );
                                                                    } else {
                                                                        mutation.mutate(cartLine.Id, {
                                                                            onSuccess: (data) => {
                                                                                incrementESIEventId();
                                                                                setEdpNo(null);
                                                                                setQuantity(null);
                                                                            },
                                                                        });
                                                                    }
                                                                    // mutation.mutate(item.Id);
                                                                }}
                                                                Update={null}
                                                            />
                                                            <div className="mb-5 border-0 border-b border-solid border-tscgrey"></div>
                                                        </Fragment>
                                                    );
                                                }
                                            } else {
                                                // Show <ItemCard/> but don't adjust quantity
                                                return (
                                                    <Fragment key={item?.CartLineItem.EdpNo}>
                                                        <ItemCard
                                                            CartLine={item}
                                                            CartProducts={cart.Products}
                                                            Remove={() => {
                                                                mutation.mutate(item.Id, {
                                                                    onSuccess: (data) => {
                                                                        incrementESIEventId();
                                                                    },
                                                                });
                                                            }}
                                                            Update={null}
                                                        />
                                                        <div className="mb-5 border-0 border-b border-solid border-tscgrey"></div>
                                                    </Fragment>
                                                );
                                            }
                                        })}
                                    </>
                                )}
                            </div>
                        </div>

                        <div
                            className={cn(
                                'flex-shrink-0 bg-gray-50 px-7 py-3',
                                isAutoFreeShipApplied || autoFreeShipAwayFromThresholdAmount > 0 ? 'h-52' : 'h-44',
                            )}
                        >
                            <div className="grid grid-cols-1">
                                <div className="mb-1.5 flex h-6 flex-col justify-center font-bold">
                                    <div className="flex justify-between">
                                        <div>
                                            Subtotal ({cart?.OrderSummary?.TotalQuantity}{' '}
                                            {cart?.OrderSummary?.TotalQuantity > 1 ? 'items' : 'item'}){' '}
                                        </div>
                                        <div>
                                            {cart?.OrderSummary?.MerchandiseSubTotal == null
                                                ? '$0.00'
                                                : cart?.OrderSummary?.MerchandiseSubTotal}
                                            {donationInCart ? <sup>*</sup> : null}
                                        </div>
                                    </div>
                                    {donationInCart ? (
                                        <div className="mb-2 font-normal">*includes donation item</div>
                                    ) : null}
                                </div>

                                <FreeShippingEarned
                                    delayedPercentage={delayedPercentage}
                                    cart={cart}
                                    freeShippingThresholdNumber={freeShippingThresholdNumber}
                                    isAutoFreeShipApplied={isAutoFreeShipApplied}
                                    isRogersCCFreeShipApplied={isRogersCCFreeShipApplied}
                                    autoFreeShipAwayFromThresholdAmount={autoFreeShipAwayFromThresholdAmount}
                                />

                                <button
                                    className="inline-flex items-center justify-center border border-solid border-border bg-black px-3 py-3 text-sm font-medium text-primary-foreground shadow transition-colors hover:font-semibold focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:border-[#e3e3e3] disabled:bg-[#e3e3e3] disabled:text-[#4a4a4a]"
                                    type="submit"
                                    aria-label="Check balance"
                                    onClick={() => checkout()}
                                >
                                    Proceed To Checkout
                                </button>
                                <div id="esiFlyoutMessage" className="mt-2"></div>
                            </div>
                        </div>
                    </div>
                    {updateMutation.isLoading || isMutating ? (
                        <div className="absolute right-0 top-0 h-full w-full">
                            <div className="absolute h-full w-full cursor-wait bg-slate-50 opacity-60"></div>
                            <div className="relative left-1/3 top-1/2 w-1/3 cursor-wait text-center">
                                <LoadingSvg />
                            </div>
                        </div>
                    ) : null}
                </SheetContent>
            </Sheet>
            {showFraudModal && <FraudModal onCloseClickListener={() => setShowFraudModal(false)} />}
        </>
    );
};

export default AddToBag;
